import React from 'react'
import { connect } from 'react-redux'
import { ConfigProvider, theme as antTheme } from 'antd'
import Loader from './Elements/Loader'
import Home from './Pages/Home/Home'
import Forgot from './Pages/Forgot/Forgot'
import Reset from './Pages/Forgot/Reset'
import { AuthModalProvider, AuthRoute } from './Auth/AuthModal'
import Authors from './Pages/Authors/Authors'
import Package from './Pages/Package/Package'
import { Routes, Route, Navigate } from 'react-router-dom'
import Header from './Layout/Header/Header'
import Footer from './Layout/Footer/Footer'
import Packages from './Pages/Packages/Packages'
import Search from './Pages/Search/Search'
import Soon from './Soon'
import { loadingOn, loadingOff } from './../redux/actions/index'
import SessionManager from './SessionManager'
import RequireAuth from './RequireAuth'
import RequireRole from './RequireRole'
import AuthorPage from './Pages/Authors/AuthorPage'
import AuthorThemes from './Pages/Authors/AuthorThemes'
import EditorsPackages from './Pages/Packages/EditorsPackages'
import TagPackages from './Pages/Packages/TagPackages'
import LegacyPageRedirect from './LegacyPageRedirect'
import NoIndexRoutes from './NoIndexRoutes'
import FeatureRequestLauncher from './Elements/FeatureRequestLauncher'
import ReleaseBanner from './Elements/ReleaseBanner'
import ScrollToTop from './ScrollToTop'
import TournamentPackages from './Pages/Package/TournamentPackages'
import Contact from './Pages/Contact/Contact'
import Order from './Pages/Contact/Order'
import { Info } from './Pages/Info/Info'
import { Confirm } from './Pages/Info/Confirm'
import Profile from './Pages/Profile/Profile'
import ManualGame from './Pages/ManualGame/ManualGame'
import UserStats from './Pages/UserStats/UserStats'
import Timer from './Pages/Timer/Timer'
import TimerWidgetDemo from './Pages/Timer/TimerWidgetDemo'
import Ratings from './Pages/Ratings/Ratings'
import RatingHistory from './Pages/Ratings/RatingHistory'
import TournamentRatingBreakdown from './Pages/Ratings/TournamentRatingBreakdown'
import RatingMethodology from './Pages/Ratings/RatingMethodology'
import RatingAdminHome from './Pages/Ratings/RatingAdminHome'
import RatingAdminCertification from './Pages/Ratings/RatingAdminCertification'
import RatingAdminRecalculate from './Pages/Ratings/RatingAdminRecalculate'
import Buzzer from './Pages/Buzzer/Buzzer'
import SheetViewer from './Pages/SheetViewer/SheetViewer'
import Scoring from './Pages/Scoring/Scoring'
import Tools from './Pages/Tools/Tools'
import Initials from './Pages/Initials/Initials'
import AppealManager from './Pages/TournamentPanel/Appeals/AppealManager'
import RatingAdminResults from './Pages/Ratings/RatingAdminResults'
import OrganizerPortal from './Pages/Ratings/OrganizerPortal'
import ProfileAppeals from './Pages/Profile/ProfileAppeals'
import LiveGame from './Pages/LiveGame/LiveGame'
import QuestionPage from './Pages/QuestionPage/QuestionPage'
import ThemePage from './Pages/ThemePage/ThemePage'
import RandomPackage from './Pages/RandomPackage/RandomPackage'
import Privacy from './Pages/Legal/Privacy'
import Terms from './Pages/Legal/Terms'
import Copyright from './Pages/Legal/Copyright'
import VerifyReference from './Pages/VerifyReference/VerifyReference'
import { isFeatureEnabled } from '../services/flagsService'
import { ThemeContext, useThemeProvider } from '@/hooks/useTheme'

function ThemeProvider({ children }: { children: React.ReactNode }) {
  const themeValue = useThemeProvider()
  return (
    <ThemeContext.Provider value={themeValue}>
      {children}
    </ThemeContext.Provider>
  )
}

function AntConfigWrapper({ children }: { children: React.ReactNode }) {
  // We read the dark class directly from the DOM for Ant Design
  const isDark = document.documentElement.classList.contains('dark')
  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: 'rgb(24, 144, 255)',
        },
        algorithm: isDark ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
      }}
    >
      {children}
    </ConfigProvider>
  )
}

// Routes that only exist while the USER_ACCOUNTS flag is on. When it is off they
// are redirected home so deep links / bookmarks don't land on a blank page.
const ACCOUNT_ROUTE_PATHS = [
  '/register',
  '/login',
  '/forgot',
  '/reset-password',
  '/reset-password/:code',
  '/reset-password/:code/:email',
  '/confirm',
  '/confirm/:code',
  '/confirm/:code/:email',
  '/profile',
  '/profile/:id',
  '/profile/appeals',
]

class App extends React.Component<any> {
  state = {
    visible: false,
  }

  render() {
    if (isFeatureEnabled('COMING_SOON')) {
      return <Soon />
    }

    const accountsEnabled = isFeatureEnabled('USER_ACCOUNTS')

    return (
      <ThemeProvider>
        <AntConfigWrapper>
          <AuthModalProvider>
          {this.props.loader > 0 ? <Loader /> : null}
          {accountsEnabled && <SessionManager />}
          <ScrollToTop />
          <NoIndexRoutes />
          <Header />
          <Routes>
            <Route path="/authors" element={<Authors />} />
            <Route path="/authors/:id/questions" element={<AuthorPage />} />
            <Route path="/authors/:id/themes" element={<AuthorThemes />} />
            {/* Legacy path-based pagination → ?page=N */}
            <Route path="/authors/:page" element={<LegacyPageRedirect to={() => '/authors'} />} />
            <Route path="/authors/:id/questions/:page" element={<LegacyPageRedirect to={id => `/authors/${id}/questions`} />} />
            <Route path="/authors/:id/themes/:page" element={<LegacyPageRedirect to={id => `/authors/${id}/themes`} />} />
            <Route path="/authors/:id/edits" element={<EditorsPackages />} />
            <Route path="/search" element={<Search />} />
            <Route path="/" element={<Home />} />
            {accountsEnabled && (
              <Route
                path="/register"
                element={!this.props.user.isLoggedIn ? <AuthRoute mode="register"><Home /></AuthRoute> : <Navigate to="/" replace />}
              />
            )}
            <Route path="/packages" element={<Packages />} />
            <Route
              path="/packages/random"
              element={isFeatureEnabled('RANDOM_PACKAGE') ? <RandomPackage /> : <Navigate to="/" replace />}
            />
            <Route path="/packages/:id" element={<Package />} />
            <Route path="/packages/:id/tournament" element={<TournamentPackages />} />
            <Route
              path="/tags/:id"
              element={isFeatureEnabled('PACKAGE_TAG_PAGES') ? <TagPackages /> : <Navigate to="/" replace />}
            />
            {accountsEnabled && (
              <Route
                path="/forgot"
                element={!this.props.user.isLoggedIn ? <Forgot /> : <Navigate to="/" replace />}
              />
            )}
            {accountsEnabled && <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>} />}
            {accountsEnabled && <Route path="/profile/:id" element={<Profile />} />}
            {accountsEnabled && <Route path="/profile/appeals" element={<RequireAuth><ProfileAppeals /></RequireAuth>} />}
            {accountsEnabled && <Route path="/reset-password" element={<Reset />} />}
            {accountsEnabled && <Route path="/reset-password/:code" element={<Reset />} />}
            {accountsEnabled && <Route path="/reset-password/:code/:email" element={<Reset />} />}
            {accountsEnabled && (
              <Route
                path="/login"
                element={!this.props.user.isLoggedIn ? <AuthRoute mode="login"><Home /></AuthRoute> : <Navigate to="/" replace />}
              />
            )}
            <Route path="/success" element={<Info />} />
            {accountsEnabled && <Route path="/confirm" element={<Confirm />} />}
            {accountsEnabled && <Route path="/confirm/:code" element={<Confirm />} />}
            {accountsEnabled && <Route path="/confirm/:code/:email" element={<Confirm />} />}
            <Route path="/contact" element={<Contact />} />
            <Route path="/order" element={<Order />} />
            <Route path="/game/:id?" element={<ManualGame />} />
            <Route path="/leaderboard" element={<UserStats />} />
            <Route path="/timer" element={<Timer />} />
            <Route path="/timer/widget" element={<TimerWidgetDemo />} />
            <Route path="/buzzer" element={<Buzzer />} />
            <Route
              path="/sheets"
              element={isFeatureEnabled('SHEET_VIEWER') ? <SheetViewer /> : <Navigate to="/" replace />}
            />
            <Route
              path="/scoring"
              element={isFeatureEnabled('SCORING_BOARD') ? <Scoring /> : <Navigate to="/" replace />}
            />
            <Route
              path="/initials"
              element={isFeatureEnabled('INITIALS_GAME') ? <Initials /> : <Navigate to="/" replace />}
            />
            <Route path="/tools" element={<Tools />} />
            <Route path="/tournament-panel" element={<Navigate to="/tools" replace />} />
            {isFeatureEnabled('TOURNAMENT_PANEL') && (
              <Route path="/tournament-panel/appeals" element={<AppealManager />} />
            )}
            <Route path="/ratings" element={<Ratings />} />
            <Route path="/ratings/:entityType/:id" element={<RatingHistory />} />
            <Route path="/ratings/tournaments/:id" element={<TournamentRatingBreakdown />} />
            <Route path="/ratings/methodology" element={<Navigate to="/ratings" replace />} />
            <Route path="/ratings/admin" element={<RequireRole role="superadmin"><RatingAdminHome /></RequireRole>} />
            <Route path="/ratings/admin/submissions" element={<Navigate to="/ratings/admin" replace />} />
            <Route path="/ratings/admin/certification" element={<RequireRole role="superadmin"><RatingAdminCertification /></RequireRole>} />
            <Route path="/ratings/admin/appeals" element={<Navigate to="/profile/appeals" replace />} />
            <Route path="/ratings/admin/recalculate" element={<RequireRole role="superadmin"><RatingAdminRecalculate /></RequireRole>} />
            <Route path="/ratings/admin/results" element={<RequireRole role="superadmin"><RatingAdminResults /></RequireRole>} />
            <Route path="/organizer" element={<RequireRole role="organizer"><OrganizerPortal /></RequireRole>} />
            <Route path="/games/:slug" element={<LiveGame />} />
            <Route path="/questions/:id" element={<QuestionPage />} />
            <Route path="/themes/:id" element={<ThemePage />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/copyright" element={<Copyright />} />
            {isFeatureEnabled('ACCOUNT_VERIFICATION') && (
              <Route path="/verify-reference/:token" element={<VerifyReference />} />
            )}
            {!accountsEnabled &&
              ACCOUNT_ROUTE_PATHS.map((path) => (
                <Route key={path} path={path} element={<Navigate to="/" replace />} />
              ))}
          </Routes>
          <Footer />
          <FeatureRequestLauncher />
          <ReleaseBanner />
          </AuthModalProvider>
        </AntConfigWrapper>
      </ThemeProvider>
    )
  }
}

const mapStateToProps = ({ loader, user }) => {
  return {
    loader,
    user,
  }
}

export default connect(mapStateToProps, { loadingOn, loadingOff })(
  App
)
